import { mergeAggregationGroups } from "@/components/controls/DataTable/groupSummaries";
import { AggregationField, GenericAggregations } from "@/services/api/types";
import React, { useMemo } from "react";
import BottomKPIBar, {
  KPI
} from "@/components/controls/BottomKPIBar/BottomKPIBar";
import { find, isEmpty } from "lodash";
import { useArrMrr } from "@/services/context/ArrMrrContext/hook";

interface CompaniesKPIData extends Record<string, number> {
  total: number;
  arrMrr: number;
  avgArrMrr: number;
  acv: number;
  tcv: number;
}

interface Props {
  aggregations: GenericAggregations<string> | {};
  totalCompanies: number;
  isLoading?: boolean;
  isError?: boolean;
  isRowsError?: boolean;
  onOpenStateChange?: (isOpen: boolean) => void;
}

const CompaniesBottomKPIBar: React.FC<Props> = ({
  aggregations,
  totalCompanies,
  isLoading = false,
  isError = false,
  isRowsError = false,
  onOpenStateChange
}) => {
  const { fieldName: arrOrMrrField, isLoading: arrMrrLoading } = useArrMrr();

  // Withhold the basis-dependent tiles until the basis is known. The count
  // comes from the rows request; everything else from the aggregations. Each
  // tile follows its own request's failure.
  const kpis: KPI<CompaniesKPIData>[] = [
    { name: "total", isRowsDerived: true },
    ...(arrMrrLoading
      ? []
      : [
          { name: "arrMrr" as const, isMonetary: true },
          { name: "avgArrMrr" as const, isMonetary: true }
        ]),
    { name: "acv", isMonetary: true },
    { name: "tcv", isMonetary: true }
  ];

  const getFieldFromData = (
    aggregationsData: AggregationField<string>[],
    fieldName: string,
    operation: "avg" | "sum"
  ) => {
    const agg = find(aggregationsData, (field) => field.name === fieldName);
    return agg?.aggregations?.[operation] || 0;
  };

  const data: CompaniesKPIData = useMemo(() => {
    let arrMrr = 0;
    let avgArrMrr = 0;
    let acv = 0;
    let tcv = 0;

    // The response is grouped whenever the table is, so the KPI figures come
    // from every group merged back together rather than from the first one.
    const aggregationsData = mergeAggregationGroups(
      aggregations as GenericAggregations<string>
    )?.fields ?? [];

    if (!isEmpty(aggregationsData)) {
      arrMrr = getFieldFromData(aggregationsData, arrOrMrrField, "sum");
      avgArrMrr = getFieldFromData(aggregationsData, arrOrMrrField, "avg");
      acv = getFieldFromData(aggregationsData, "annualContractValue", "sum");
      tcv = getFieldFromData(aggregationsData, "totalContractValue", "sum");
    }

    return {
      total: totalCompanies,
      arrMrr,
      avgArrMrr,
      acv,
      tcv
    };
  }, [aggregations, totalCompanies, arrOrMrrField]);

  return (
    <BottomKPIBar<CompaniesKPIData>
      kpis={kpis}
      data={data}
      pageName="companies"
      isLoading={isLoading}
      isError={isError}
      isRowsError={isRowsError}
      onOpenStateChange={onOpenStateChange}
    />
  );
};

export default CompaniesBottomKPIBar;
